import { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearError, clearMessage, getUser,
    loginUser, logOutUser, registerUser, updatePassword, updateUser,
	updateUserSettings,
	logInWithGoogle,
	logOutbackend
} from '../store/auth-slice';
import { BASE_URL, logger } from '../lib/utils';
import { useToast } from '../hooks/use-toast';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
    const { user, isLoading, error, message } = useSelector(state => state.auth);
    const { toast } = useToast();
    const dispatch = useDispatch();

    const register = useCallback((data) => {
        dispatch(registerUser(data));
    }, [dispatch]);

    const login = useCallback((data) => {
        dispatch(loginUser(data));
    }, [dispatch]);

    const authUsingGithub = useCallback(() => {
        window.location.href = `${BASE_URL}/auth/github`;
    }, []);

    const loginWithToken = useCallback(async (token) => {
		try {
			localStorage.setItem('token', token);
        	await dispatch(getUser()).unwrap();
			toast({
				title: 'Login successful',
				description: 'You have successfully logged in with GitHub.',
				variant: 'success',
			});
		} catch (error) {
			toast({
				title: 'Login failed',
				description: error.message || 'GitHub login failed. Please try again.',
				variant: 'destructive',
			});
		}
    }, [dispatch, toast]);

    const logout = useCallback(() => {
		dispatch(logOutbackend())
        dispatch(logOutUser());
    }, [dispatch]);
	const UpdateUserSetting = useCallback((data) => {
		dispatch(updateUserSettings(data));
	}, [dispatch]);
    const UpdatePassword = useCallback((data) => {
        dispatch(updatePassword(data));
    }, [dispatch]);

    const UpdateUser = useCallback((data) => {
        dispatch(updateUser(data));
    }, [dispatch]);
	const loginWithGoogleToken = useCallback(async(token) => {
		dispatch(logInWithGoogle(token));
	}, [dispatch]);

    useEffect(() => {
		const token =
			localStorage.getItem("token") || sessionStorage.getItem("token");

		if (!token) return;
		dispatch(getUser());
	}, [dispatch]);
	useEffect(()=>{
		if(error){
			toast({
				title: "Authentication error",
				description: error || "An error occurred during authentication.",
				variant: "destructive",
			});
			const timer = setTimeout(() => {
				dispatch(clearError());
			},3000)
			return () => clearTimeout(timer);
		}
	},[error,toast, dispatch])
	useEffect(()=>{
		if(message){
			toast({
				title: "Success",
				description: message,
				variant: "success",
			});
			const timer = setTimeout(() => {
				dispatch(clearMessage());
			},3000)
			return () => clearTimeout(timer);
		}
	}, [message, toast, dispatch]);
	
	const value = useMemo(()=>{
		return{
			user, 
			isLoading,
			error, 
			message,
			loginWithToken, 
			login, 
			register, 
			authUsingGithub, 
			logout, 
			UpdatePassword, 
			UpdateUser, 
			UpdateUserSetting,
			loginWithGoogleToken,
		}
	},[
		user, 
		isLoading,
		error, 
		message,
		loginWithToken, 
		login, 
		register, 
		authUsingGithub, 
		logout, 
		UpdatePassword, 
		UpdateUser, 
		UpdateUserSetting,
		loginWithGoogleToken,
	])
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
